import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { authApi } from '@/api/modules/auth'
import type { LoginParams, GoogleLoginParams } from '@/api/modules/auth'
import { getToken, removeToken, setToken } from '@/api/request'
import { useMiningStore } from '@/stores'

export type { LoginParams, GoogleLoginParams }

export interface User {
  id: string
  uid: string
  email: string
  nickname: string
  avatar?: string
  referralCode: string
  referredBy?: string
  balance?: { BTC: number; BLD: number }
  totalEarnings?: number
  authMethod?: 'email' | 'google'
  clientOS?: string
  pwaInstalled?: boolean
}

export interface AuthResult {
  success: boolean
  error?: string
  code?: string
  isNewUser?: boolean
}

export interface AuthContext {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (params: LoginParams) => Promise<AuthResult>
  register: (params: LoginParams) => Promise<AuthResult>
  googleLogin: (params: GoogleLoginParams) => Promise<AuthResult>
  refreshUser: () => Promise<void>
  logout: () => void
}

const AuthReactContext = createContext<AuthContext | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [hasToken, setHasToken] = useState(() => Boolean(getToken()))
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = Boolean(user) || hasToken

  useEffect(() => {
    const token = getToken()
    if (!token) {
      setIsLoading(false)
      return
    }
    void authApi
      .getMe()
      .then((res) => {
        if (res.success && res.data?.user) {
          setUser(res.data.user as User)
        } else {
          removeToken()
          setHasToken(false)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const login = useCallback(async (params: LoginParams): Promise<AuthResult> => {
    const res = await authApi.login(params)
    if (res.success && res.data) {
      setToken(res.data.token)
      setHasToken(true)
      setUser(res.data.user as User)
      return { success: true }
    }
    return { success: false, error: res.error || 'Login failed', code: res.code }
  }, [])

  const register = useCallback(async (params: LoginParams): Promise<AuthResult> => {
    const res = await authApi.register(params)
    if (res.success && res.data) {
      setToken(res.data.token)
      setHasToken(true)
      setUser(res.data.user as User)
      return { success: true }
    }
    return { success: false, error: res.error || 'Registration failed', code: res.code }
  }, [])

  const googleLogin = useCallback(async (params: GoogleLoginParams): Promise<AuthResult> => {
    const res = await authApi.googleLogin(params)
    if (res.success && res.data) {
      setToken(res.data.token)
      setHasToken(true)
      setUser(res.data.user as User)
      return { success: true, isNewUser: res.data.isNewUser }
    }
    return { success: false, error: res.error || 'Google login failed', code: res.code }
  }, [])

  const refreshUser = useCallback(async (): Promise<void> => {
    const res = await authApi.getMe()
    if (res.success && res.data?.user) {
      setUser(res.data.user as User)
    }
  }, [])

  const logout = useCallback((): void => {
    removeToken()
    setHasToken(false)
    setUser(null)
    useMiningStore.getState().reset()
  }, [])

  const value = useMemo<AuthContext>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      googleLogin,
      refreshUser,
      logout,
    }),
    [user, isAuthenticated, isLoading, login, register, googleLogin, refreshUser, logout],
  )

  return createElement(AuthReactContext.Provider, { value }, children)
}

export function useAuth(): AuthContext {
  const ctx = useContext(AuthReactContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
